import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useUpdate } from "../mutation";
import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";
import { useQueryListing } from "./useQueryListing";
import { usePhase } from "../../projectOverview/hooks/usePhase";
import { useSearchProjects } from "../../projects/mutations";

export const useQueryAndRFI = (projectId?: string) => {

    const update = useUpdate();
    const phaseData = usePhase();
    const searchProjects = useSearchProjects();

    const [filter, setFilter] = useState<{
        phase: null | string,
        assigned: null | string,
        category: null | string,
        project: null | AutocompleteOption,
        portfolio: null | AutocompleteOption,
        programme: null | AutocompleteOption,
        priority: null | any,
        status: null | any
    }>({
        project: null,
        portfolio: null,
        programme: null,
        phase: null,
        assigned: null,
        priority: null,
        category: null,
        status: null
    });

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const [searchDraft, setSearchDraft] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('');
    const [filterDrawer, setFilterDrawer] = useState(false);

    const [raiseQuery, setRaiseQuery] = useState<{
        open: boolean,
        status: boolean,
        id: null | string,
    }>({
        open: false,
        status: false,
        id: null
    });

    const [queryDetails, setQueryDetails] = useState<{
        status: boolean,
        data: null | {
            id: string,
            title: string,
            description: string,
            status: string,
            raised_by: string,
            assigned_to: string,
            submitted_date: Date,
        },
        id: null | string,
    }>({
        status: false,
        data: null,
        id: null
    });

    const handleUpdateFilter = (key: string, value: string) => {
        setFilter({
            ...filter,
            [key]: value
        })
    };

    const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchDraft(e.target.value);
    };

    const handleClear = () => {
        setSearchDraft('');
        setSearchKey('');
    };

    const handleSearch = () => {
        setSearchKey(searchDraft);
    };

    const changePage = (page: number) => {
        setPage(page);
    };

    const changeRowsPerPage = (limit: number) => {
        setPage(1);
        setLimit(limit);
    };

    const onEdit = async (id: string) => {
        setRaiseQuery({ open: true, status: true, id });
        setQueryDetails({
            status: false,
            data: null,
            id: null
        });
    };

    const onClose = () => {
        setRaiseQuery({
            open: false,
            status: false,
            id: null
        });
    };
    
    const activeProjectId = useMemo(() => {
        return filter.project?.id;
    }, [filter.project?.id]);

    const onResolved = async (queryId: string) => {
        const payload = {
            "status": "Closed"
        };
        await update.mutateAsync({ queryId, payload });
        setQueryDetails({
            status: false,
            data: null,
            id: null
        });
        await refetch();
    };


    const onApply = async () => {
        setFilterDrawer(false);
        await refetch();
    };

    const onClear = async (status: boolean) => {
        setFilterDrawer(false);
        if (status) {
            if (projectId) {
                setFilter({
                    ...filter,
                    portfolio: null,
                    programme: null,
                    phase: null,
                    assigned: null,
                    priority: null,
                    category: null,
                    status: null
                });
            } else {
                setFilter({
                    project: null,
                    portfolio: null,
                    programme: null,
                    phase: null,
                    assigned: null,
                    priority: null,
                    category: null,
                    status: null
                });
            }
            await refetch();
        }
    };

    const fetchProjectDetails = async () => {
        const params: Record<string, any> = {
            project_id: projectId
        };
        const response = await searchProjects.mutateAsync({ params });
        setFilter({
            ...filter,
            project: {
                id: response.data[0].id,
                label: response.data[0].title
            }
        });
    };

    const { pagination, isLoading, isFetching, data, stateData, refetch } = useQueryListing({
        page, limit, searchKey,
        category: filter.category,
        status: filter.status,
        priority: filter.priority,
        user_id: filter.assigned,
        project_id: filter.project,
        project_tree_id: filter.phase,
        portfolio_id: filter.portfolio,
        programme_id: filter.programme
    });
    
    useEffect(() => {
        if (projectId)
            fetchProjectDetails();
    }, [projectId]);

    useEffect(() => {
        if (activeProjectId)
            phaseData.mutateAsync(activeProjectId);
    }, [activeProjectId]);

    return {
        isFetching, isLoading,
        data, stateData,
        searchDraft, handleChangeSearch, handleClear, handleSearch,
        setFilterDrawer, setRaiseQuery,
        pagination, changePage, changeRowsPerPage,
        queryDetails, setQueryDetails,
        onResolved, onEdit, raiseQuery,
        phases: phaseData,
        isPhasePending: phaseData.isPending,
        onClose, refetch, filterDrawer, filter, handleUpdateFilter, onClear, onApply
    };

};